import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MobileLayout } from '../components/MobileLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { SharingOpportunitiesWidget } from '../components/SharingOpportunitiesWidget';
import { Plus, X, Check, MapPin, Users, CalendarClock, Sparkles, Zap } from 'lucide-react';
import { useMaintenance, RequestStatus, MaintenanceRequest, Trade } from '../context/MaintenanceContext';
const STATUS_BADGE: Record<RequestStatus, {
  label: string;
  cls: string;
}> = {
  in_attesa_aggregazione: {
    label: 'In attesa di aggregazione',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  in_ricerca: {
    label: 'In ricerca fornitore',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  assegnata: {
    label: 'Assegnata',
    cls: 'bg-muted text-muted-foreground border-border'
  },
  pianificata: {
    label: 'Pianificata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  in_corso: {
    label: '● In corso',
    cls: 'bg-coral/5 text-coral border-coral/20'
  },
  completata: {
    label: 'Completata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
const PROPERTIES = ['Loft Navigli', 'Bilo Porta Romana', 'Studio Isola'];
const PROPERTY_ZONE: Record<string, string> = {
  'Loft Navigli': 'Navigli',
  'Bilo Porta Romana': 'Porta Romana',
  'Studio Isola': 'Isola'
};
const TRADES: Trade[] = ['Idraulico', 'Elettricista', 'Caldaista', 'Tuttofare', 'Fabbro'];
const inputClass = 'w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]';
const labelClass = 'block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5';
// Two-option segmented control
function Segmented<T extends string>({
  options,
  value,
  onChange
}: {
  options: {
    id: T;
    label: string;
  }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return <div className="grid grid-cols-2 gap-1 p-1 bg-muted rounded-xl" data-id="element-3512">
      {options.map(o => {
      const active = o.id === value;
      return <button key={o.id} type="button" onClick={() => onChange(o.id)} aria-pressed={active} className={`h-9 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-3513">
            {o.label}
          </button>;
    })}
    </div>;
}
// ---- Create-request bottom sheet ----
function CreateSheet({
  open,
  onClose,
  onCreate
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (input: Parameters<ReturnType<typeof useMaintenance>['createRequest']>[0]) => void;
}) {
  const [mode, setMode] = useState<'pulizia' | 'urgente'>('pulizia');
  const [property, setProperty] = useState(PROPERTIES[0]);
  // pulizia
  const [periodMode, setPeriodMode] = useState<'singola' | 'periodo'>('singola');
  const [date, setDate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tipo, setTipo] = useState<'ordinaria' | 'straordinaria'>('ordinaria');
  // urgente
  const [trade, setTrade] = useState<Trade>(TRADES[0]);
  // shared
  const [detail, setDetail] = useState('');
  const resetForm = () => {
    setMode('pulizia');
    setProperty(PROPERTIES[0]);
    setPeriodMode('singola');
    setDate('');
    setDateFrom('');
    setDateTo('');
    setTipo('ordinaria');
    setTrade(TRADES[0]);
    setDetail('');
  };
  // Close on Escape + lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'pulizia') {
      const isStraordinaria = tipo === 'straordinaria';
      const when = periodMode === 'singola' ? date || 'Da definire' : dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : 'Da definire';
      onCreate({
        title: isStraordinaria ? 'Pulizia straordinaria' : 'Pulizia ordinaria',
        detail,
        property,
        zone: PROPERTY_ZONE[property],
        type: 'pulizia-straordinaria',
        urgency: isStraordinaria ? 'urgente' : 'normale',
        price: isStraordinaria ? 120 : 80,
        date: when
      });
    } else {
      onCreate({
        title: detail.trim() || 'Manutenzione urgente',
        detail,
        property,
        zone: PROPERTY_ZONE[property],
        type: 'manutenzione',
        trade,
        urgency: 'urgente',
        price: 90,
        date: 'Oggi'
      });
    }
    resetForm();
    onClose();
  };
  return <AnimatePresence data-id="element-3514">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center lg:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }} data-id="element-3515">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" data-id="element-3516" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Nuovo intervento" initial={{
        y: '100%',
        opacity: 0.6
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: '100%',
        opacity: 0.6
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 320
      }} className="relative w-full lg:max-w-lg bg-white rounded-t-2xl lg:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto" data-id="element-3517">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-border px-5 py-4 flex items-center justify-between" data-id="element-3518">
              <h2 className="text-base font-bold text-navy" data-id="element-3519">
                Nuovo intervento
              </h2>
              <button type="button" onClick={onClose} aria-label="Chiudi" className="p-2 -mr-2 rounded-full hover:bg-muted transition-colors" data-id="element-3520">
                <X size={20} className="text-muted-foreground" data-id="element-3521" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-5" data-id="element-3522">
              {/* Type chooser */}
              <Segmented value={mode} onChange={setMode} options={[{
            id: 'pulizia',
            label: 'Pulizia'
          }, {
            id: 'urgente',
            label: 'Urgente'
          }]} data-id="element-3523" />

              {/* Property */}
              <div data-id="element-3524">
                <label className={labelClass} data-id="element-3525">Proprietà</label>
                <select value={property} onChange={e => setProperty(e.target.value)} className={inputClass} data-id="element-3526">
                  {PROPERTIES.map(p => <option key={p} value={p} data-id="element-3527">
                      {p}
                    </option>)}
                </select>
              </div>

              {mode === 'pulizia' ? <>
                  {/* Quando */}
                  <div data-id="element-3528">
                    <label className={labelClass} data-id="element-3529">Quando</label>
                    <Segmented value={periodMode} onChange={setPeriodMode} options={[{
                id: 'singola',
                label: 'Singola giornata'
              }, {
                id: 'periodo',
                label: 'Periodo'
              }]} data-id="element-3530" />
                    {periodMode === 'singola' ? <div className="mt-3" data-id="element-3531">
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} aria-label="Data" data-id="element-3532" />
                      </div> : <div className="mt-3 grid grid-cols-2 gap-3" data-id="element-3533">
                        <div data-id="element-3534">
                          <span className="block text-[11px] text-muted-foreground mb-1" data-id="element-3535">
                            Dal
                          </span>
                          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputClass} aria-label="Dal" data-id="element-3536" />
                        </div>
                        <div data-id="element-3537">
                          <span className="block text-[11px] text-muted-foreground mb-1" data-id="element-3538">
                            Al
                          </span>
                          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputClass} aria-label="Al" data-id="element-3539" />
                        </div>
                      </div>}
                  </div>

                  {/* Tipo */}
                  <div data-id="element-3540">
                    <label className={labelClass} data-id="element-3541">Tipo di pulizia</label>
                    <Segmented value={tipo} onChange={setTipo} options={[{
                id: 'ordinaria',
                label: 'Ordinaria'
              }, {
                id: 'straordinaria',
                label: 'Straordinaria'
              }]} data-id="element-3542" />
                  </div>
                </> : <div data-id="element-3543">
                  <label className={labelClass} data-id="element-3544">Tipo di fornitore</label>
                  <select value={trade} onChange={e => setTrade(e.target.value as Trade)} className={inputClass} data-id="element-3545">
                    {TRADES.map(t => <option key={t} value={t} data-id="element-3546">
                        {t}
                      </option>)}
                  </select>
                </div>}

              {/* Detail */}
              <div data-id="element-3547">
                <label className={labelClass} data-id="element-3548">Cosa serve</label>
                <textarea value={detail} onChange={e => setDetail(e.target.value)} rows={3} placeholder={mode === 'pulizia' ? 'Es. Deep cleaning prima del prossimo check-in' : 'Es. Perdita rubinetto in cucina'} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none resize-none" data-id="element-3549" />
              </div>

              <Button className="w-full" type="submit" data-id="element-3550">
                Invia richiesta
              </Button>
            </form>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// ---- Compact shared-aggregation row ----
function SharedRow({
  agg,
  joined,
  onJoin
}: {
  agg: MaintenanceRequest;
  joined: boolean;
  onJoin: () => void;
}) {
  const filled = agg.aggregation?.filled ?? 0;
  const total = agg.aggregation?.total ?? 0;
  const display = joined ? Math.min(filled + 1, total) : filled;
  return <div className="bg-white border border-border rounded-xl p-4 flex items-center justify-between gap-3" data-id="element-3551">
      <div className="min-w-0" data-id="element-3552">
        <h3 className="text-sm font-semibold text-foreground truncate" data-id="element-3553">
          {agg.title}
        </h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 flex-wrap" data-id="element-3554">
          <span className="inline-flex items-center gap-1" data-id="element-3555">
            <MapPin size={12} data-id="element-3556" />
            {agg.zone}
          </span>
          <span className="inline-flex items-center gap-1 text-coral font-medium" data-id="element-3557">
            <Users size={12} data-id="element-3558" />
            {display}/{total} host
          </span>
          {agg.aggregation?.estimateDate && <span className="inline-flex items-center gap-1" data-id="element-3559">
              <CalendarClock size={12} data-id="element-3560" />
              {agg.aggregation.estimateDate}
            </span>}
        </div>
      </div>
      {joined ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 flex-shrink-0" data-id="element-3561">
          <Check size={15} data-id="element-3562" />
          Unito
        </span> : <button onClick={onJoin} className="px-3 h-9 bg-coral text-white rounded-lg text-sm font-semibold hover:bg-coral-600 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-3563">
          Unisciti
        </button>}
    </div>;
}
export function RichiediInterventoPage() {
  const {
    requests,
    openAggregations,
    createRequest,
    joinAggregation
  } = useMaintenance();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [joined, setJoined] = useState<string[]>([]);
  const [showAllShared, setShowAllShared] = useState(false);
  const [activeTab, setActiveTab] = useState<'richieste' | 'pulizie' | 'manutenzioni'>('richieste');
  const visibleShared = useMemo(() => showAllShared ? openAggregations : openAggregations.slice(0, 3), [openAggregations, showAllShared]);
  // Auto-open the create sheet when navigated here from the header's
  // "Nuovo intervento" button.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if ((location.state as {
      openNew?: boolean;
    } | null)?.openNew) {
      setSheetOpen(true);
      navigate(location.pathname, {
        replace: true,
        state: {}
      });
    }
  }, [location, navigate]);
  return <MobileLayout data-id="element-3564">
      {/* Header + primary CTA */}
      <div className="mb-6 flex items-center justify-between gap-3" data-id="element-3565">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-3566">
          Richiedi intervento
        </h1>
        <Button className="flex-shrink-0" onClick={() => setSheetOpen(true)} data-id="element-3567">
          <Plus size={18} data-id="element-3568" />
          <span className="hidden sm:inline" data-id="element-3569">Nuovo intervento</span>
          <span className="sm:hidden" data-id="element-3570">Nuovo</span>
        </Button>
      </div>

      {/* Section tabs */}
      <Tabs className="mb-6" scrollable value={activeTab} onValueChange={id => setActiveTab(id as 'richieste' | 'pulizie' | 'manutenzioni')} items={[{
      id: 'richieste',
      label: 'Le mie richieste'
    }, {
      id: 'pulizie',
      label: 'Pulizie condivise'
    }, {
      id: 'manutenzioni',
      label: 'Manutenzioni condivise'
    }]} data-id="element-3571" />

      {/* TAB: Le mie richieste */}
      {activeTab === 'richieste' && (requests.length > 0 ? <div className="space-y-3" data-id="element-3572">
            {requests.map(r => <Card key={r.id} data-id="element-3573">
                <CardContent className="flex items-start justify-between gap-3" data-id="element-3574">
                  <div className="min-w-0" data-id="element-3575">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3576">
                      <h3 className="text-sm font-semibold text-foreground truncate" data-id="element-3577">
                        {r.title}
                      </h3>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${STATUS_BADGE[r.status].cls}`} data-id="element-3578">
                        {STATUS_BADGE[r.status].label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap" data-id="element-3579">
                      <span className="flex items-center gap-1" data-id="element-3580">
                        <MapPin size={12} data-id="element-3581" />
                        {r.property} · {r.zone}
                      </span>
                      {r.aggregation && <span className="text-coral font-medium" data-id="element-3582">
                          {r.aggregation.filled}/{r.aggregation.total} host
                        </span>}
                      {r.providerName && <span data-id="element-3583">· {r.providerName}</span>}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-foreground flex-shrink-0" data-id="element-3584">
                    €{r.price}
                  </span>
                </CardContent>
              </Card>)}
          </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-3585">
            <p className="text-sm font-medium text-foreground" data-id="element-3586">
              Nessuna richiesta ancora
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3587">
              Tocca “Nuovo intervento” per crearne una.
            </p>
          </div>)}

      {/* TAB: Pulizie condivise */}
      {activeTab === 'pulizie' && <div data-id="element-3588">
          <SharingOpportunitiesWidget data-id="element-3589" />
        </div>}

      {/* TAB: Manutenzioni condivise */}
      {activeTab === 'manutenzioni' && (openAggregations.length > 0 ? <div data-id="element-3590">
            <div className="flex items-baseline justify-between mb-3" data-id="element-3591">
              <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-id="element-3592">
                <Users size={13} className="text-coral flex-shrink-0 mt-0.5" data-id="element-3593" />
                Unisciti a una richiesta aperta: al raggiungimento del minimo di
                host l’intervento viene pianificato e il costo è condiviso.
              </p>
              {openAggregations.length > 3 && <button onClick={() => setShowAllShared(v => !v)} className="text-xs font-medium text-coral hover:underline flex-shrink-0 ml-3" data-id="element-3594">
                  {showAllShared ? 'Mostra meno' : 'Vedi tutte'}
                </button>}
            </div>
            <div className="space-y-3" data-id="element-3595">
              {visibleShared.map(agg => <SharedRow key={agg.id} agg={agg} joined={joined.includes(agg.id)} onJoin={() => {
          joinAggregation(agg.id);
          setJoined(p => [...p, agg.id]);
        }} data-id="element-3596" />)}
            </div>
          </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-3597">
            <p className="text-sm font-medium text-foreground" data-id="element-3598">
              Nessuna manutenzione condivisa in zona
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3599">
              Quando altri host aprono una richiesta condivisa, la vedrai qui.
            </p>
          </div>)}

      {/* Create sheet */}
      <CreateSheet open={sheetOpen} onClose={() => setSheetOpen(false)} onCreate={input => createRequest(input)} data-id="element-3600" />
    </MobileLayout>;
}